import React, { useState } from "react";
import { Button, Badge, Table } from "flowbite-react";
import { HiOutlineCalendar, HiOutlineDocumentText } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import ShowSingleRoadmapModal from "../../RoadMapNew/ShowSingleRoadmapModal";
import { 
  getStatusIcon,
  getStatusColor,
  getStatusBorderColor,
  getProgressColor,
  formatStatusForDisplay
} from "./StatusUtils";
import { 
  getSourceFromObject, 
  getEpicTitle,
  getEpicEntityName, 
  getFeatureEntityName,
  getCompletionPercentage,
  Epic,
  Feature
} from "./SourceUtils";

interface EpicDetailCardProps {
  epic: Epic;
  selectFeature: (feature: Feature) => void;
  openRoadmap?: (roadmapId: string) => void;
  currentRoadmap?: any; // Added for API data
  projectName: string;
  openDashboard: (epicId: string) => void;
}

const EpicDetailCard: React.FC<EpicDetailCardProps> = ({ 
  epic, 
  selectFeature, 
  openRoadmap,
  currentRoadmap = null,
  projectName,
  openDashboard
}) => {
  const navigate = useNavigate();
  const [showRoadmapDrawer, setShowRoadmapDrawer] = useState(false);
  const roadmapId = currentRoadmap && epic.id ; // Use epic ID if available
  
  const handleOpenRoadmap = () => {
    if (openRoadmap) {
      // Instead of navigating, open the drawer
      setShowRoadmapDrawer(true);
    }
  };

  // Get the source from epic
  const source = getSourceFromObject(epic);

  // Get the child entity name directly using the shared utility function
  const childEntityName = getFeatureEntityName(source);

  // Get button text for roadmap view based on source
  const roadmapButtonText = source === 'azure' 
    ? 'View Strategic Map' 
    : `View ${getEpicEntityName(source)}`;

  // Convert API project to Feature format
  const convertProjectToFeature = (project: any): Feature => {
    return {
      id: project.id,
      name: project.name,
      description: "",
      status: project.status,
      startDate: new Date(project.start_date).toLocaleDateString(),
      endDate: new Date(project.end_date).toLocaleDateString(),
      tasksCompleted: 0, // Default values
      totalTasks: 0,
      blockers: 0,
      peopleAllocated: 0
    };
  };

  // Get features to display - either from mock data or API
  const getFeatures = () => {
    if (currentRoadmap && currentRoadmap.projects) {
      return currentRoadmap.projects.map(convertProjectToFeature);
    }
    return epic.features;
  };

  const features = getFeatures();

  // Get stats data
  const getStatsData = () => {
    if (currentRoadmap && currentRoadmap.stats) {
      const stats = currentRoadmap.stats;
      return {
        total: stats.total_projects || 0,
        onTrack: stats.on_track_projects || 0,
        completed: stats.completed_projects || 0,
        delayed: stats.delayed_projects || 0,
        unallocated: stats.unallocated_projects || 0
      };
    }
    
    // Default stats from epic object
    return {
      total: epic.features.length,
      onTrack: epic.features.filter(f => formatStatusForDisplay(f.status) === "On Track").length,
      completed: epic.features.filter(f => formatStatusForDisplay(f.status) === "Completed").length,
      delayed: epic.features.filter(f => formatStatusForDisplay(f.status) === "Delayed").length,
      unallocated: epic.features.filter(f => formatStatusForDisplay(f.status) === "Unallocated").length
    };
  };

  const stats = getStatsData();

  return (
    <>
      <div className="min-w-[400px] max-w-[400px] bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ maxHeight: "600px" }}>
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex justify-between items-center">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            {getEpicTitle(source)}
          </h3>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge color="info" className={getStatusColor(epic.status)}>
                {formatStatusForDisplay(epic.status)}
              </Badge>
              <span className="text-sm text-slate-500 dark:text-gray-300">
                {epic.startDate} - {epic.endDate}
              </span>
            </div>

            <h4 className="font-medium text-lg text-slate-900 dark:text-white">
              {getEpicEntityName(source)}: {epic.name}
            </h4>

            {/* {currentRoadmap && currentRoadmap.description && (
              <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-slate-900 dark:text-white">Description</h4>
                <div 
                  className="text-sm text-slate-700 dark:text-slate-300"
                  dangerouslySetInnerHTML={{ __html: currentRoadmap.description }}
                />
              </div>
            )} */}

            <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
              <h4 className="font-medium mb-3 text-slate-900 dark:text-white">
                {getEpicEntityName(source)} Summary
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 dark:text-gray-300">
                    People Allocated
                  </div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-white">
                    {currentRoadmap ? currentRoadmap.people_allocated : epic.peopleAllocated || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 dark:text-gray-300">
                    {`Total ${childEntityName}s`}
                  </div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-white">
                    {stats.total}
                  </div>
                </div>
                
                {!currentRoadmap && (
                  <>
                    <div>
                      <div className="text-xs text-slate-500 dark:text-gray-300">
                        {`${childEntityName}s with Blockers`}
                      </div>
                      <div className="text-lg font-semibold text-slate-900 dark:text-white">
                        {epic.features.filter((f) => typeof f.blockers === 'number' && f.blockers > 0).length}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 dark:text-gray-300">
                        Total Blockers
                      </div>
                      <div className="text-lg font-semibold text-slate-900 dark:text-white">{epic.blockers}</div>
                    </div>
                  </>
                )}
              </div>
              
              {/* Progress bar */}
              {currentRoadmap && typeof currentRoadmap.progress === 'number' && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-slate-500 dark:text-gray-300">Overall Progress</div>
                    <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      {(currentRoadmap.progress).toFixed(0)}%
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700">
                    <div 
                      className={`h-full rounded-full ${getProgressColor(currentRoadmap.progress)}`}
                      style={{ width: `${currentRoadmap.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
              <h4 className="font-medium mb-2 text-slate-900 dark:text-white">
                {`${childEntityName} Status`}
              </h4>

              {!currentRoadmap && (
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">On Track</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.onTrack}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">Delayed</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.delayed}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">Completed</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.completed}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-slate-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">Unallocated</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.unallocated}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {currentRoadmap && stats.total > 0 && (
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">On Track</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.onTrack}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">Delayed</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.delayed}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">Completed</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.completed}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-slate-500"></div>
                      <span className="text-sm text-slate-700 dark:text-gray-300">Unallocated</span>
                      <span className="text-sm font-medium ml-auto text-slate-900 dark:text-white">
                        {stats.unallocated}
                      </span>
                    </div>
                  </div>
                  
                  {/* Visual distribution bar */}
                  <div className="mt-4">
                    <div className="text-xs text-slate-500 dark:text-gray-400 mb-2">Status Distribution</div>
                    <div className="flex h-4 rounded-md overflow-hidden">
                      {stats.onTrack > 0 && (
                        <div 
                          className="bg-emerald-500" 
                          style={{ width: `${(stats.onTrack / stats.total) * 100}%` }}
                          title={`On Track: ${stats.onTrack}`}
                        ></div>
                      )}
                      {stats.completed > 0 && (
                        <div 
                          className="bg-blue-500" 
                          style={{ width: `${(stats.completed / stats.total) * 100}%` }}
                          title={`Completed: ${stats.completed}`}
                        ></div>
                      )}
                      {stats.delayed > 0 && (
                        <div 
                          className="bg-amber-500" 
                          style={{ width: `${(stats.delayed / stats.total) * 100}%` }}
                          title={`Delayed: ${stats.delayed}`}
                        ></div>
                      )}
                      {stats.unallocated > 0 && (
                        <div 
                          className="bg-slate-500" 
                          style={{ width: `${(stats.unallocated / stats.total) * 100}%` }}
                          title={`Unallocated: ${stats.unallocated}`}
                        ></div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2 mt-3">
                {features.map((feature: Feature) => (
                  <div
                    key={feature.id}
                    className={`p-2 rounded-md cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-600 ${getStatusBorderColor(feature.status)}`}
                    onClick={() => selectFeature(feature)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900 dark:text-white truncate max-w-[75%]">{feature.name}</span>
                      <Badge
                        color="info"
                        className={getStatusColor(feature.status)}
                      >
                        {getStatusIcon(feature.status)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <Button
            className="w-full"
            color="light"
            onClick={handleOpenRoadmap}
          >
            <HiOutlineCalendar className="h-4 w-4 mr-2" />
            {roadmapButtonText}
          </Button>
        </div>
      </div>
      
      {/* Roadmap Drawer */}
      <ShowSingleRoadmapModal 
        isVisible={showRoadmapDrawer}
        setShowModal={setShowRoadmapDrawer}
        roadmap_id={roadmapId}
      />
    </>
  );
};

export default EpicDetailCard;